/**
 * Color utilities for hex normalization, CIE-LAB conversion, and CIE76 distance.
 *
 * Uses the same conversion constants as BambuStudio and amsUtils.ts:
 *   pivotRgb × 100, D65 illuminant (REF_X=95.047, REF_Y=100.0, REF_Z=108.883)
 */

export type LAB = { L: number; a: number; b: number };

// ---------------------------------------------------------------------------
// Hex → RGB
// ---------------------------------------------------------------------------

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  if (!hex) return { r: 0, g: 0, b: 0 };
  let clean = hex.trim();
  if (clean[0] !== '#') clean = `#${clean}`;

  // Expand shorthand #RGB → #RRGGBB
  if (clean.length === 4) {
    const r = clean[1];
    const g = clean[2];
    const b = clean[3];
    clean = `#${r}${r}${g}${g}${b}${b}`;
  }

  // Strip alpha from #RRGGBBAA
  if (clean.length === 9) clean = clean.slice(0, 7);

  const r = parseInt(clean.slice(1, 3), 16) || 0;
  const g = parseInt(clean.slice(3, 5), 16) || 0;
  const b = parseInt(clean.slice(5, 7), 16) || 0;
  return { r, g, b };
}

// ---------------------------------------------------------------------------
// Hex normalization
// ---------------------------------------------------------------------------

const HEX_RE = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

/**
 * Normalize a hex color string to uppercase "RRGGBB" (no # prefix, no alpha).
 * Returns undefined for empty/invalid input.
 */
export function normalizeHexColor(input: unknown): string | undefined {
  if (typeof input !== 'string' || !input) return undefined;
  const trimmed = input.trim();
  if (!HEX_RE.test(trimmed)) return undefined;

  let clean = trimmed;
  if (clean[0] === '#') clean = clean.slice(1);
  clean = clean.toUpperCase();

  // Expand shorthand RGB → RRGGBB
  if (clean.length === 3) {
    clean = clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
  }

  // Strip alpha
  if (clean.length === 8) clean = clean.slice(0, 6);

  return clean;
}

/**
 * Normalize a hex color string to uppercase "RRGGBBAA".
 * If input is 6-char (no alpha), appends "FF".
 * Returns undefined for empty/invalid input.
 */
export function normalizeHexColorWithAlpha(input: unknown): string | undefined {
  if (typeof input !== 'string' || !input) return undefined;
  const trimmed = input.trim();
  if (!HEX_RE.test(trimmed)) return undefined;

  let clean = trimmed;
  if (clean[0] === '#') clean = clean.slice(1);
  clean = clean.toUpperCase();

  // Expand shorthand RGB → RRGGBB
  if (clean.length === 3) {
    clean = clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
  }

  // Append alpha if missing
  if (clean.length === 6) clean += 'FF';

  return clean;
}

// ---------------------------------------------------------------------------
// RGB → CIE-LAB (D65 illuminant, BambuStudio compatible)
// ---------------------------------------------------------------------------

function pivotRgb(n: number): number {
  return (n > 0.04045 ? Math.pow((n + 0.055) / 1.055, 2.4) : n / 12.92) * 100.0;
}

function pivotXyz(n: number): number {
  return n > 0.008856 ? Math.cbrt(n) : 7.787 * n + 16.0 / 116.0;
}

const REF_X = 95.047;
const REF_Y = 100.0;
const REF_Z = 108.883;

export function rgbToLab(r: number, g: number, b: number): LAB {
  const R = pivotRgb(r / 255);
  const G = pivotRgb(g / 255);
  const B = pivotRgb(b / 255);

  const X = 0.412453 * R + 0.35758 * G + 0.180423 * B;
  const Y = 0.212671 * R + 0.71516 * G + 0.072169 * B;
  const Z = 0.019334 * R + 0.119193 * G + 0.950227 * B;

  const x = pivotXyz(X / REF_X);
  const y = pivotXyz(Y / REF_Y);
  const z = pivotXyz(Z / REF_Z);

  return {
    L: 116 * y - 16,
    a: 500 * (x - y),
    b: 200 * (y - z),
  };
}

export function hexToLab(hex: string): LAB {
  const { r, g, b } = hexToRgb(hex);
  return rgbToLab(r, g, b);
}

// ---------------------------------------------------------------------------
// CIE76 color distance
// ---------------------------------------------------------------------------

export function deltaE76(lab1: LAB, lab2: LAB): number {
  const dl = lab1.L - lab2.L;
  const da = lab1.a - lab2.a;
  const db = lab1.b - lab2.b;
  return Math.hypot(dl, da, db);
}

export function getColorDistance(hex1: string, hex2: string): number {
  return deltaE76(hexToLab(hex1), hexToLab(hex2));
}

export function isColorWithinThreshold(hex1: string, hex2: string, threshold: number): boolean {
  return getColorDistance(hex1, hex2) <= threshold;
}
